import type { FAQItem } from './types';

export const pricingFAQs: FAQItem[] = [
  {
    id: 'native-app-costs',
    question: 'Are there costs for a mobile app or app store publishing?',
    answer: `No. The platform is a web application. We do not provide or plan native mobile apps, so there are no Apple/Google developer or app store publishing fees. A full installable, offline-capable Progressive Web App experience is a design goal, not built yet.`,
    category: 'pricing',
    featured: true,
  },
  {
    id: 'transparent-pricing-structure',
    question: 'How does your transparent pricing structure work?',
    answer: `We believe in complete transparency. Your monthly bill has two clear components:

**Platform Service Fees (Fixed Monthly):**
• Silver: $8/parcel/month - Core community management platform
• Gold: $10/parcel/month - Advanced features + analytics  
• Custom: Quote-based - Enterprise features + customization

**Data Usage Fees (Variable Monthly):**
• Calculated based on actual community usage patterns
• Covers storage, bandwidth, and media streaming costs
• Transparent monthly billing with detailed usage reports
• Estimated range: $0.10-$1.50 per parcel (varies significantly by usage)

*Why separate? Just like your electric bill scales with usage, data costs vary based on how actively your community uses features like video content, large file sharing, and marketplace activity. This ensures you only pay for what you actually use.*

**Note:** This is a web application (PWA). There are no native mobile apps, so you avoid Apple/Google developer accounts and app store publishing fees.
`,
    category: 'pricing',
    featured: true,
  },
  {
    id: 'data-usage-explained',
    question: "Why can't you just include data costs in the platform fee?",
    answer: `That would mean light users subsidize heavy users, which isn't fair. A community that primarily uses text documents shouldn't pay the same data costs as one with extensive video libraries. Our approach ensures everyone pays only for what they actually consume.

**What affects data usage:**
• Video content and streaming in marketplace
• Large file sharing and document storage
• High-resolution photos and media
• Backup and archival storage requirements
• Geographic requirements (US-only storage costs more)

Most communities see very predictable patterns after the first 2-3 months.`,
    category: 'pricing',
    featured: true,
  },
  {
    id: 'usage-budgeting',
    question: 'How do we budget for unpredictable data costs?',
    answer: `We provide detailed usage forecasts during setup, monthly trend reports, and optional spending caps. Most communities see very predictable patterns after the first 2-3 months. Plus, you have tools to control usage if needed.

**Protection against surprise bills:**
• Good-faith usage estimates during onboarding
• Monthly usage caps with notifications at 75% and 90%
• Usage increases >50% trigger automatic notification
• 5 business days to approve increased spending or implement controls
• Detailed monthly reports showing exactly what drives costs

**Most communities see usage costs between $0.10-$1.50 per parcel monthly** including both platform and usage fees. However, actual costs depend entirely on how your community uses the platform's features.`,
    category: 'pricing',
    featured: true,
  },
  {
    id: 'pricing-structure',
    question: 'How much does this cost and what does each resident pay?',
    answer: `Pricing is transparent and based on per-parcel costs:

**Silver Package:**
• $8 per parcel/month platform service fee
• $24,360 setup fee (one-time, upfront)
• *For 406 parcels: $3,248/month platform fee, $38,976/year, plus the $24,360 setup*

**Gold Package:**
• $10 per parcel/month platform service fee
• $24,360 setup fee (one-time, upfront), the same as Silver
• *For 406 parcels: $4,060/month platform fee, $48,720/year, plus the $24,360 setup*

**Important:** Platform service fees are fixed monthly costs. Data usage fees (storage, bandwidth, streaming) are billed separately based on actual community usage patterns—estimated $0.10-$1.50 per parcel monthly.

Most communities add this cost to monthly HOA dues. Individual residents don't pay separately—the community pays as a single entity.`,
    category: 'pricing',
  },
  {
    id: 'infrastructure-customizations',
    question: 'What if we need our data stored only in the United States?',
    answer: `We absolutely can accommodate that! Infrastructure customizations like US-only data storage, enhanced security compliance, or dedicated resources are available but require custom pricing based on your specific usage patterns and requirements.

**Common customizations that require quotes:**
• US-only data storage and processing
• Enhanced compliance requirements
• Dedicated server resources
• Custom backup and retention policies
• Multi-region redundancy

These requirements are scoped during the pre-signing process with detailed cost estimates based on your community's expected usage patterns. Pricing varies significantly based on data volume, activity levels, and specific compliance needs.`,
    category: 'pricing',
  },
  {
    id: 'governance-model-choice',
    question: 'Should we mirror our existing governance structure or adopt your digital model?',
    answer: `While we can mirror your existing analog governance structure, our design goal is a digital governance model that is more effective for most communities once built. Here's the intent:

**Digital Governance Design Goals:**
• **Digitally Enforceable (planned):** Decisions recorded with a complete audit trail and automatically implementable
• **Seamless Integration (planned):** Voting, proposals, and implementation working together in one system
• **Dispute Resolution (planned):** Audit trails and cross-referencing
• **Cost Effective:** No expensive analog-to-digital translation required
• **Future Proof:** Built for modern community management

**Analog Governance Migration Challenges:**
• **Dual Authority Issues:** Conflicts between digital system and paper bylaws
• **Expensive Translation:** Converting cumbersome bylaws into programmatic code
• **Ongoing Complexity:** Constant cross-referencing between digital and analog systems
• **Limited Enforceability:** Analog authority cannot be digitally automated

Ask us for current build status on the specific governance features you need before choosing an approach.`,
    category: 'governance',
    featured: true,
  },
  {
    id: 'data-provider-independence',
    question: 'Can you control data and infrastructure costs?',
    answer: `As a community service provider, we cannot control third-party infrastructure costs. We use enterprise-grade providers (AWS, CloudFlare) whose pricing is beyond our control - just like how your community can't control utility company rates.

**What we can't control:**
• Cloud storage pricing changes
• Bandwidth and egress fees
• Streaming service cost fluctuations
• Compliance infrastructure requirements

**What we can control:**
• Our platform service fees (with 90-day notice for changes)
• Helping you optimize usage patterns
• Providing detailed cost breakdowns and forecasts
• Working with you to reduce unnecessary usage

This transparent approach protects both your community and our business from unpredictable infrastructure costs.`,
    category: 'pricing',
  },
  {
    id: 'termination-terms',
    question: 'Can we cancel early? What fees apply?',
    answer: `Early termination is available with notice and fees per the agreement:

• Silver: 90 days notice; early termination fee equal to 50% of remaining monthly fees in the current term
• Gold: 120 days notice; early termination fee equal to 75% of remaining monthly fees in the current term
• Enterprise: governed by the enterprise agreement

These terms reflect the significant upfront implementation investment.`,
    category: 'pricing',
  },
  {
    id: 'setup-fees-explained',
    question: 'Why are setup fees so high?',
    answer: `Setup fees cover extensive professional services that typically take 200+ hours:

• **System configuration** for your specific community structure
• **Data migration** from existing systems and documents  
• **Custom branding** and platform customization
• **Role-based access setup** for different user types
• **Governance integration** mapping your bylaws into the system
• **Comprehensive training** for board, residents, and vendors
• **Testing and validation** to ensure everything works correctly

This is enterprise-level software implementation, not a simple app setup. The setup fee ensures your community gets a fully customized, working system from day one.`,
    category: 'pricing',
  },
  {
    id: 'ongoing-costs',
    question: 'Are there any hidden costs or additional fees?',
    answer: `No hidden costs. The monthly platform fee covers all standard features and support. Data usage fees are completely transparent with detailed monthly reports.

**What We DON'T Charge (Unlike Competitors):**
• ❌ **No voting fees** — Competitors often charge per vote, per ballot, or per election. We include unlimited voting.
• ❌ **No transaction fees** — No fees for work orders, document access, facility bookings, or resident actions.
• ❌ **No per-seat charges** — Unlimited users, admins, and residents included.
• ❌ **No surprise usage fees** — Data usage is transparent with monthly caps and notifications.

**Optional Additional Costs (Clearly Outlined):**
• **Data usage fees** (transparent, usage-based billing with caps)
• **Additional storage** (beyond tier allocation, once storage metering is built)
• **Custom integrations** (if requested)
• **Emergency on-site support** (Professional/Enterprise tiers)
• **Infrastructure customizations** (US-only storage, enhanced compliance)
• **API access** (planned; pricing to be defined once a public API is built)

All additional services require pre-approval and clear pricing. No surprise bills.`,
    category: 'pricing',
  },
  {
    id: 'contract-terms',
    question: 'What are the contract terms and can we cancel?',
    answer: `Contract terms are designed for long-term community success:

• **Initial term:** 3 years (ensures ROI on setup investment)
• **Auto-renewal:** 1-2 year terms unless 90-120 days notice given
• **Early termination:** Possible with notice and early termination fees
• **All fees non-refundable** except as specifically stated

Early termination fees help cover the significant setup investment. Most communities find the platform becomes essential to operations within the first year.`,
    category: 'pricing',
  },
];
